/**
 * Predictor.jsx
 * How a draft compares with what has already worked for this account.
 *
 * The name is the most dangerous thing about this screen, so the screen argues
 * with it throughout. It does not predict reach, clicks or sign-ups — it
 * cannot, and a precise-looking figure would be the most persuasive wrong
 * thing in the app. What it shows is where a draft sits against the brand's
 * own published posts, and what comparable posts actually did.
 *
 * Scoring is free: it reads analytics already collected and does arithmetic.
 * The AI review of hook and call to action, and the rewrites, are the only two
 * things here that spend, and both are buttons.
 *
 * Dependencies: services/api.js, components/predictor/*, hooks/useBrands.jsx
 */

import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api.js';
import { useBrands, useSelectedBrand } from '../hooks/useBrands.jsx';
import { Chart, Check, Edit, Sparkle, Warning } from '../components/Icons.jsx';
import { PLATFORMS } from '../components/dashboard/helpers.js';
import NoBrands from '../components/NoBrands.jsx';
import Score from '../components/predictor/Score.jsx';

const FORMATS = [
  ['post', 'Single post'],
  ['carousel', 'Carousel'],
  ['story', 'Story'],
  ['link', 'Link post'],
];

const EMPTY = { caption: '', first_comment: '', format: 'post', when_local: '', media_count: 0 };

export default function Predictor() {
  const navigate = useNavigate();
  const { brands, loading: brandsLoading } = useBrands();
  const [brandId] = useSelectedBrand();

  const [platform, setPlatform] = useState('instagram');
  const [available, setAvailable] = useState([]);
  const [draft, setDraft] = useState(EMPTY);

  const [baseline, setBaseline] = useState(null);
  const [result, setResult] = useState(null);
  const [judged, setJudged] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [variations, setVariations] = useState([]);

  const [scoring, setScoring] = useState(false);
  const [critiquing, setCritiquing] = useState(false);
  const [rewriting, setRewriting] = useState(false);
  const [error, setError] = useState(null);
  const [note, setNote] = useState(null);

  // What there is to compare against, answered before anything is scored so
  // the screen can say up front how much history it has.
  useEffect(() => {
    if (!brandId) return;
    let stopped = false;

    (async () => {
      try {
        const [base, plat] = await Promise.all([
          api.predict.baseline({ brandId, platform }),
          api.predict.platforms(brandId),
        ]);
        if (stopped) return;
        setBaseline(base);
        setAvailable(plat.platforms);
        setError(null);
      } catch (e) {
        if (!stopped) setError(e);
      }
    })();

    return () => {
      stopped = true;
    };
  }, [brandId, platform]);

  // A judgement belongs to the words it was made about. Editing the caption
  // retires it rather than letting a stale "Hook: Excellent" sit beside new
  // text it has never seen.
  const edit = (patch) => {
    setDraft((d) => ({ ...d, ...patch }));
    if (patch.caption !== undefined && judged.length) {
      setJudged([]);
      setSuggestions([]);
      setNote('Caption changed, so the AI review of the hook and call to action has been cleared.');
    }
  };

  const score = useCallback(
    async (over = {}) => {
      if (!brandId) return;
      setScoring(true);
      setError(null);
      try {
        setResult(
          await api.predict.score({ brandId, platform, draft: { ...draft, ...over }, judged }),
        );
      } catch (e) {
        setError(e);
      } finally {
        setScoring(false);
      }
    },
    [brandId, platform, draft, judged],
  );

  /** The paid half: the two things no amount of history can measure. */
  async function critique() {
    setCritiquing(true);
    setError(null);
    setNote(null);
    try {
      const res = await api.predict.critique({ brandId, platform, draft });
      setJudged(res.factors);
      setSuggestions(res.suggestions);
      setNote(`Reviewed for about £${(res.costPence / 100).toFixed(2)}.`);

      // Re-score at once so the hook and CTA join the overall figure.
      setResult(await api.predict.score({ brandId, platform, draft, judged: res.factors }));
    } catch (e) {
      setError(e);
    } finally {
      setCritiquing(false);
    }
  }

  /** Rewrites, each scored by the same measured factors as the original. */
  async function rewrite() {
    setRewriting(true);
    setError(null);
    setNote(null);
    try {
      const res = await api.predict.variations({ brandId, platform, draft, count: 2 });
      setVariations(res.variations);
      setNote(`Two rewrites, about £${(res.costPence / 100).toFixed(2)}. Nothing has been saved.`);
    } catch (e) {
      setError(e);
    } finally {
      setRewriting(false);
    }
  }

  if (!brandsLoading && brands.length === 0) return <NoBrands />;

  const platforms = available.length ? available : ['instagram', 'facebook'];

  return (
    <>
      <div className="page-head page-head--hero">
        <div>
          <div className="page-head__eyebrow">Analyse &amp; grow</div>
          <h1>
            <span className="copilot-mark">
              <Chart size={26} />
            </span>
            Content check <span className="badge badge--accent">Beta</span>
          </h1>
          <p>
            See how a draft compares with your own published posts before you commit to it —
            caption length, format, posting time and hashtags, measured against what has actually
            worked for this account.
          </p>
        </div>

        <div className="page-head__actions">
          <button className="btn" onClick={() => setDraft(EMPTY)} disabled={scoring}>
            Clear
          </button>
          <button
            className="btn btn--primary"
            onClick={() => score()}
            disabled={scoring || !draft.caption.trim()}
            title="Free — this reads figures already collected and does arithmetic."
          >
            <Check size={16} /> {scoring ? 'Comparing…' : 'Assess draft'}
          </button>
        </div>
      </div>

      {/* Said before anything else on the screen, because the name promises
          more than the feature can deliver. */}
      <div className="notice notice--info">
        <Warning />
        <span>
          This does not predict views, clicks or sign-ups, and deliberately shows no such figure.
          It compares your draft with posts you have already published and tells you where it
          sits.
        </span>
      </div>

      {error && (
        <div className="notice notice--error">
          <Warning />
          <span>{error.message}</span>
        </div>
      )}

      {note && (
        <div className="notice notice--info">
          <Sparkle />
          <span>{note}</span>
        </div>
      )}

      <div className="predict-grid">
        <section className="card">
          <div className="card__head">
            <span className="tile">
              <Edit size={21} />
            </span>
            <div className="card__head-text">
              <h2>Draft</h2>
              <p>Paste or write the post you are thinking of publishing.</p>
            </div>
          </div>

          <div className="platform-picker">
            {platforms.map((p) => {
              const meta = PLATFORMS[p];
              if (!meta) return null;
              return (
                <button
                  key={p}
                  type="button"
                  className={`platform-pick${platform === p ? ' is-active' : ''}`}
                  onClick={() => setPlatform(p)}
                >
                  <meta.Icon size={16} /> {meta.label}
                </button>
              );
            })}
          </div>

          <div className="field">
            <label htmlFor="caption">Caption</label>
            <textarea
              id="caption"
              rows={9}
              value={draft.caption}
              maxLength={5000}
              placeholder="Write the post…"
              onChange={(e) => edit({ caption: e.target.value })}
            />
            <span className="hint">{draft.caption.length} characters</span>
          </div>

          <div className="field">
            <label htmlFor="first_comment">Hashtags / first comment</label>
            <textarea
              id="first_comment"
              rows={2}
              value={draft.first_comment}
              maxLength={2000}
              placeholder="#one #two #three"
              onChange={(e) => edit({ first_comment: e.target.value })}
            />
          </div>

          <div className="field-row">
            <div className="field">
              <label htmlFor="format">Format</label>
              <select
                id="format"
                value={draft.format}
                onChange={(e) => edit({ format: e.target.value })}
              >
                {FORMATS.map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>

            <div className="field">
              <label htmlFor="when">Intended time</label>
              <input
                id="when"
                type="datetime-local"
                value={draft.when_local}
                onChange={(e) => edit({ when_local: e.target.value })}
              />
            </div>
          </div>

          {baseline && (
            <p className="card__footnote">
              {baseline.measured} measured {platform} posts to compare against
              {baseline.newest
                ? `, from ${new Date(baseline.oldest).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} to ${new Date(baseline.newest).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`
                : ''}
              . {baseline.timing?.fallback ? baseline.timing.message : ''}
            </p>
          )}
        </section>

        <Score
          result={result}
          baseline={baseline}
          onCritique={critique}
          critiquing={critiquing}
          hasCritique={judged.length > 0}
        />

        <section className="card">
          <div className="card__head">
            <span className="tile">
              <Sparkle size={21} />
            </span>
            <div className="card__head-text">
              <h2>What to change</h2>
              <p>Specific edits, from the AI review.</p>
            </div>
          </div>

          {suggestions.length === 0 ? (
            <div className="empty empty--inline">
              <div className="empty__art">
                <Sparkle size={26} />
              </div>
              <h3>No review yet</h3>
              <p>
                Assess the draft, then add the AI review to get suggestions on the hook and the
                call to action — the two things your history cannot measure.
              </p>
            </div>
          ) : (
            <ol className="suggest-list">
              {suggestions.map((s, i) => (
                <li key={i}>
                  <span className="suggest-list__n">{i + 1}</span>
                  <div>
                    <div className="suggest-list__text">{s.text}</div>
                    {s.why && <div className="suggest-list__why">{s.why}</div>}
                  </div>
                </li>
              ))}
            </ol>
          )}

          <div className="btn-row">
            <button
              className="btn"
              onClick={rewrite}
              disabled={rewriting || !draft.caption.trim()}
              title="Writes two alternative captions. Counts towards your monthly AI spend."
            >
              <Sparkle size={15} /> {rewriting ? 'Rewriting…' : 'Suggest rewrites'}
            </button>
            <button className="btn" onClick={() => navigate('/create')}>
              Open Create
            </button>
          </div>
        </section>
      </div>

      {variations.length > 0 && (
        <section className="card">
          <div className="card__head">
            <span className="tile">
              <Sparkle size={21} />
            </span>
            <div className="card__head-text">
              <h2>Alternative versions</h2>
              <p>
                Each scored the same way as your draft — a rewrite is never declared better by the
                model that wrote it.
              </p>
            </div>
          </div>

          <div className="variations">
            {variations.map((v, i) => (
              <div className="variation" key={i}>
                <div className="variation__head">
                  <span className="tag tag--accent">{v.label || `Version ${i + 1}`}</span>
                  {v.assessment.overall === null ? (
                    <span className="tag tag--muted">Not enough to score</span>
                  ) : (
                    <span className={`tag tag--${v.assessment.band?.tone ?? 'muted'}`}>
                      {v.assessment.overall}/100 · {v.assessment.band?.label}
                    </span>
                  )}
                </div>
                <p className="variation__text">{v.caption}</p>
                <button
                  className="btn btn--sm"
                  onClick={() => {
                    edit({ caption: v.caption });
                    setNote('Put into the draft. Nothing has been saved or published.');
                  }}
                >
                  Use this
                </button>
              </div>
            ))}
          </div>
        </section>
      )}
    </>
  );
}
